/** @jsxImportSource preact */
import type { ComponentChildren } from "preact";

import * as m from "../../paraglide/messages.js";
import { ExternalLinkIcon, GlobeIcon, PhoneIcon } from "../icons";

/**
 * 邮件结果卡、确认卡、暂时不能发送卡、未发送行和入口卡、状态卡共用的小零件：图标圆、状态头、标签值行、商家联系方式。
 * 样式随各卡的 *_STYLES 一起进 Shadow DOM，颜色和圆角读组件根节点上的 --gn-form-* 变量。
 */

/** 商家的其他联系方式，都可以没有。 */
export interface WidgetStoreContact {
  /** 联系页。label 是给人看的短地址，如 `aurora.example/contact`；链接在新页面打开。 */
  contactPage?: { label: string; url: string };
  /** 电话，原文显示，不做成链接。 */
  phone?: string;
}

/** 联系页和电话至少给了一个。 */
export function hasStoreContact(
  contact: WidgetStoreContact | undefined,
): contact is WidgetStoreContact {
  return (
    contact !== undefined &&
    (contact.contactPage !== undefined || contact.phone !== undefined)
  );
}

/** 图标圆的三种底：ink 墨黑实心（已完成、当答案）；soft 井色（进行中、说明）；danger 危险色淡底（出了问题）；future 虚线（还没发生）。 */
export type WidgetIconTone = "danger" | "future" | "ink" | "soft";

/**
 * 36px 圆装图标（商家把圆角调成 0 时变方块）。ink 是墨黑实心加反色图标，同 Sent 卡上的对勾；
 * 一张卡里已经有墨黑主按钮时用 soft，免得出现两个墨黑答案。
 */
export function IconTile({
  children,
  tone = "ink",
}: {
  children: ComponentChildren;
  tone?: WidgetIconTone;
}) {
  return (
    <span aria-hidden="true" class="gn-tile" data-tone={tone}>
      {children}
    </span>
  );
}

/**
 * 卡片顶上的状态头：图标圆、标题和一行可选说明。
 * status 会在状态变化时礼貌播报；alert 用于已经出错、需要马上知道的。
 */
export function StatusHeader({
  icon,
  live,
  subtitle,
  title,
  tone = "ink",
}: {
  icon: ComponentChildren;
  live: "alert" | "status";
  subtitle?: string;
  title: string;
  tone?: WidgetIconTone;
}) {
  return (
    <div
      aria-live={live === "status" ? "polite" : undefined}
      class="gn-status"
      role={live}
    >
      <IconTile tone={tone}>{icon}</IconTile>
      <div class="gn-status-text">
        <div class="gn-status-title">{title}</div>
        {subtitle !== undefined && (
          <div class="gn-status-subtitle">{subtitle}</div>
        )}
      </div>
    </div>
  );
}

/** 一组标签值行（标签淡、值加粗），标签列宽由外面的 --gn-fact-label 定，默认 84px；放进 .gn-well 里用。 */
export function Facts({ children }: { children: ComponentChildren }) {
  return <dl class="gn-facts">{children}</dl>;
}

export function FactRow({ label, value }: { label: string; value: string }) {
  return (
    <div class="gn-fact">
      <dt class="gn-fact-label">{label}</dt>
      <dd class="gn-fact-value">{value}</dd>
    </div>
  );
}

/** 联系页（新页面打开）和电话，各自可以没有。 */
export function StoreContactList({ contact }: { contact: WidgetStoreContact }) {
  return (
    <ul class="gn-contact-list">
      {contact.contactPage !== undefined && (
        <li class="gn-contact-row">
          <GlobeIcon size={13} />
          <span class="gn-contact-label">
            {m.widget_unavailable_contact_page()}
          </span>
          <a
            class="gn-contact-link"
            href={contact.contactPage.url}
            rel="noopener noreferrer"
            target="_blank"
          >
            {contact.contactPage.label}
            <ExternalLinkIcon size={12} />
          </a>
        </li>
      )}
      {contact.phone !== undefined && (
        <li class="gn-contact-row">
          <PhoneIcon size={13} />
          <span>{m.widget_unavailable_phone({ number: contact.phone })}</span>
        </li>
      )}
    </ul>
  );
}

/** 图标圆的样式，入口卡、状态卡也用。 */
export const ICON_TILE_STYLES = `
.gn-tile { flex:none; box-sizing:border-box; display:flex; align-items:center; justify-content:center; width:36px; height:36px; border-radius:var(--gn-form-r-pill); background:var(--gn-form-answer); color:var(--gn-form-answer-fg); }
.gn-tile[data-tone="soft"] { background:var(--gn-form-sheet); color:var(--gn-form-ink); }
.gn-tile[data-tone="danger"] { background:color-mix(in srgb, var(--gn-form-danger) 12%, var(--gn-form-sheet)); color:var(--gn-form-danger); }
.gn-tile[data-tone="future"] { background:transparent; border:1.5px dashed var(--gn-form-dash); color:var(--gn-form-quiet); }
`;

/** 状态头、标签值行、商家联系方式的样式（含图标圆）。 */
export const CARD_PARTS_STYLES = `${ICON_TILE_STYLES}
.gn-status { display:flex; align-items:flex-start; gap:10px; padding:2px 4px 0; }
.gn-status-text { display:flex; flex-direction:column; justify-content:center; gap:2px; min-width:0; min-height:36px; }
.gn-status-title { font-size:15px; font-weight:700; letter-spacing:-.01em; line-height:1.25; }
.gn-status-subtitle { font-size:12px; line-height:1.4; color:var(--gn-form-quiet); }
.gn-facts { display:flex; flex-direction:column; gap:var(--gn-facts-gap, 4px); margin:0; }
.gn-fact { display:grid; grid-template-columns:var(--gn-fact-label, 84px) minmax(0, 1fr); gap:8px; align-items:baseline; font-size:13px; }
.gn-fact-label { margin:0; font-size:12px; font-weight:var(--gn-form-strong); color:var(--gn-form-quiet); }
.gn-fact-value { margin:0; font-weight:700; font-variant-numeric:tabular-nums; overflow-wrap:anywhere; }
.gn-contact-list { box-sizing:border-box; display:flex; flex-direction:column; gap:8px; margin:0; padding:12px; list-style:none; border-radius:var(--gn-form-r-well); background:var(--gn-form-sheet); font-size:13px; font-weight:var(--gn-form-strong); }
.gn-contact-row { display:flex; flex-wrap:wrap; align-items:center; gap:4px 8px; }
.gn-contact-row > svg { flex:none; color:var(--gn-form-quiet); }
.gn-contact-label { font-size:12px; color:var(--gn-form-quiet); white-space:nowrap; }
.gn-contact-link { display:inline-flex; align-items:center; gap:4px; color:var(--gn-form-ink); font-weight:700; text-decoration:underline; text-underline-offset:2px; }
.gn-contact-link:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:2px; border-radius:2px; }
`;
